import { useMemo, useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Amount, Button, Chip, Glass, Panel, PanelHeader, Ref, cn } from '@promanage/ui';
import {
  TRANSACTION_KINDS,
  formatNzDate,
  formatPeriod,
  formatRef,
  isCalendarDate,
  transactionKindLabel,
  type TransactionKind,
} from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { FinancialNav } from '../features/ledger/FinancialNav';
import { RemittanceButton } from '../features/bills/RemittanceButton';
import { BankFileDialog } from '../features/disbursements/BankFileDialog';
import { centsFromWire } from '../features/ledger/draft';
import { JournalDetail } from '../features/ledger/JournalDetail';
import { TransactionDetail } from '../features/ledger/TransactionDetail';
import { TransactionFilters } from '../features/ledger/TransactionFilters';
import { transactionSeq } from '../features/ledger/refRange';
import { TrustEffectRows } from '../features/ledger/TrustEffectRows';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/financial/transactions')({
  /*
   * EVERY FILTER IS IN THE URL rather than in state. Two of them arrive as LINKS — `account` from
   * the drill-through on an owner's trust position, `period` from the periods panel on the trust
   * page — and all four are things somebody reloads, bookmarks and sends to a colleague. Component
   * state survives none of the three.
   *
   * Left unvalidated beyond "is a string", the same call `/login` makes about `redirect`: an id
   * this agency does not own returns nothing under RLS, and a kind that is not a kind is refused
   * by the contract. Both are better answers than a router error page.
   */
  validateSearch: (search: Record<string, unknown>): TransactionSearch => ({
    ...(typeof search.account === 'string' && search.account !== ''
      ? { account: search.account }
      : {}),
    ...(typeof search.period === 'string' && search.period !== '' ? { period: search.period } : {}),
    ...(typeof search.kind === 'string' && isTransactionKind(search.kind)
      ? { kind: search.kind }
      : {}),
    ...(typeof search.q === 'string' && search.q.trim() !== '' ? { q: search.q } : {}),
    /*
     * The two ends of the range are shape-checked here but NOT range-checked against each other.
     * A backwards range is refused by `transactionListSchema`, which is the boundary that also
     * sees the copy-pasted URL and the stale bookmark; duplicating the rule here would be a
     * second place for it to drift, and the error the operator needs is the server's sentence
     * rather than a router page.
     */
    ...(typeof search.from === 'string' && isCalendarDate(search.from)
      ? { from: search.from }
      : {}),
    ...(typeof search.to === 'string' && isCalendarDate(search.to) ? { to: search.to } : {}),
    // A run of transactions by number. Read forgivingly and written back canonically — see
    // `transactionSeq` — so `?txFrom=tx 42` and `?txFrom=TX000042` are one bookmark, not two.
    ...refEnd('txFrom', search.txFrom),
    ...refEnd('txTo', search.txTo),
  }),
  component: TransactionsPage,
});

/** The filter, as it appears in the URL. `q` rather than `search` — it is what people type. */
interface TransactionSearch {
  account?: string;
  period?: string;
  kind?: TransactionKind;
  q?: string;
  /** The day the event happened, on or after. ISO in the URL, as it is on the wire. */
  from?: string;
  /** On or before, inclusive. */
  to?: string;
  /**
   * The first transaction of a run, as its ref — `?txFrom=TX012345`. The ref and not the bare
   * number, because this is the half of the URL a person reads and the thing they were quoted.
   */
  txFrom?: string;
  /** The last one, inclusive. */
  txTo?: string;
}

/** One end of the run off the URL, in its canonical spelling, or nothing. */
function refEnd<Key extends 'txFrom' | 'txTo'>(key: Key, value: unknown): Partial<Record<Key, string>> {
  // The router parses a bare `?txFrom=42` as a number before this sees it.
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
  const sequence = transactionSeq(text);
  return sequence === null ? {} : ({ [key]: formatRef('transaction', sequence) } as Record<Key, string>);
}

const TRANSACTION_PAGE_SIZE = 50;

function isTransactionKind(value: string): value is TransactionKind {
  return (TRANSACTION_KINDS as readonly string[]).includes(value);
}

/**
 * A row the operator may tick: money that has gone out, and has not been handed back.
 *
 * `bill_payment`, NOT `bill`, and the difference is what `0064` was for. A `bill` is the APPROVAL
 * — the owners' money committed to an invoice — and nothing has left the trust account, so there
 * is no credit to put in a file and nothing to advise a supplier of. Until the kinds were split
 * this column offered a tick on both and the server refused half of them afterwards.
 *
 * ONE PREDICATE, used by the column, by the row and by "select all", because three copies of it is
 * how a checkbox comes to appear on a row the action then refuses. What each condition is for is
 * at the call site.
 */
function isSelectablePayment(row: { kind: string; reversedBy: unknown }): boolean {
  return (row.kind === 'disbursement' || row.kind === 'bill_payment') && row.reversedBy === null;
}

/**
 * What has happened — the ledger, read back.
 *
 * ITS OWN PAGE, and that is a move rather than a new screen. It used to be the right-hand two
 * thirds of the trust ledger, which meant the page an agency opens every morning was mostly a
 * historical record with the day's actual work behind a modal. Reading is occasional; receipting
 * is the daily job. The trust page now does the work and this one holds the record.
 *
 * Product principle 4 in three hops: a figure reaches the TRANSACTION that caused it, a
 * transaction opens to the JOURNALS it posted, and a journal opens to its debits and credits.
 *
 * IT LISTS TRANSACTIONS RATHER THAN JOURNALS, which is not cosmetic. A rent receipt is one event
 * and three journals; an agency taking 200 receipts a month would otherwise be reading 600 rows
 * of accounting artefacts to find out what happened.
 */
function TransactionsPage() {
  const { account, period, kind, q, from, to, txFrom, txTo } = Route.useSearch();
  const refSeqFrom = txFrom === undefined ? null : transactionSeq(txFrom);
  const refSeqTo = txTo === undefined ? null : transactionSeq(txTo);
  const navigate = useNavigate({ from: Route.fullPath });
  const [openTransactionId, setOpenTransactionId] = useState<string | null>(null);
  const [openJournalId, setOpenJournalId] = useState<string | null>(null);
  /*
   * WHICH PAYMENTS THE OPERATOR HAS TICKED, for a bank file.
   *
   * A SET OF IDS RATHER THAN A FLAG ON EACH ROW, and pruned to what is on screen below. An
   * invisible selection is dangerous on any list and unacceptable on this one: the action at the
   * end of it produces a payment instruction, and a file quietly containing three payments the
   * operator filtered away twenty seconds ago is money moving that nobody looked at.
   */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [makingBankFile, setMakingBankFile] = useState(false);

  const transactions = useInfiniteQuery(
    trpc.ledger.transactions.infiniteQueryOptions(
      {
        limit: TRANSACTION_PAGE_SIZE,
        ...(period === undefined ? {} : { periodStartsOn: period }),
        ...(from === undefined ? {} : { occurredFrom: from }),
        ...(to === undefined ? {} : { occurredTo: to }),
        ...(refSeqFrom === null ? {} : { refSeqFrom }),
        ...(refSeqTo === null ? {} : { refSeqTo }),
        ...(account === undefined ? {} : { accountId: account }),
        ...(kind === undefined ? {} : { kind }),
        ...(q === undefined ? {} : { search: q }),
      },
      {
        meta: { errorTitle: 'Could not load the transactions' },
        getNextPageParam: (page) => page.nextCursor,
        staleTime: 10_000,
      },
    ),
  );

  // The account's own name, for the banner — "OW000003's account" is what the operator needs to
  // see, not the uuid they arrived with.
  const accounts = useQuery(
    trpc.ledger.accounts.queryOptions(undefined, {
      staleTime: 30_000,
      enabled: account !== undefined,
    }),
  );
  const accountName = accounts.data?.find((a) => a.id === account)?.name ?? null;

  const narrowed =
    account !== undefined ||
    period !== undefined ||
    kind !== undefined ||
    q !== undefined ||
    from !== undefined ||
    to !== undefined ||
    txFrom !== undefined ||
    txTo !== undefined;

  /*
   * Built once and passed to both the panel heading and the totals footer, which have to agree:
   * they are two statements of what the same figures cover, and DESIGN_SYSTEM §3 requires a
   * total to say what it covers. Two call sites building it separately is how they drift.
   */
  const rows = useMemo(
    () => transactions.data?.pages.flatMap((page) => page.rows) ?? [],
    [transactions.data],
  );
  // Totals cover the whole filter and are deliberately calculated only on the first page.
  const totals = transactions.data?.pages[0]?.totals ?? null;

  const scope = scopeOf({
    count: totals?.transactionCount ?? 0,
    period: period ?? null,
    account: account === undefined ? null : (accountName ?? 'one account'),
    kind: kind ?? null,
    search: q ?? null,
    from: from ?? null,
    to: to ?? null,
    txFrom: txFrom ?? null,
    txTo: txTo ?? null,
  });

  /*
   * WHICH ROWS MAY BE TICKED, decided here and offered nowhere else.
   *
   * TWO KINDS OF PAYMENT OUT, and each has a document at the end of it. A DISBURSEMENT pays an
   * owner and becomes a line of a bank file. A BILL payment pays a supplier's invoice and becomes
   * a line of their remittance advice — either from a supplier run, forty at a time on the 20th,
   * or from `payBill` on its own. Both are money that has left, and the operator's question about
   * both is the same one: what did we pay, and what do I send about it.
   *
   * REVERSED IS EXCLUDED FROM BOTH, and it is the condition that would be missed. A reversed
   * payment is one the books have already handed back: the row is still here, correctly, because a
   * reversal never touches the original (invariant 4). Paying it again would send money the ledger
   * says was not received, and advising a supplier of it would have them mark an invoice settled
   * that is still owing. The server refuses both; this is what stops the tick.
   */
  const payable = useMemo(() => rows.filter(isSelectablePayment), [rows]);

  /*
   * The selection, narrowed to what is actually listed. A filter change, a page reload or a new
   * transaction arriving all leave ticks behind, and a document made from ids nobody can see is
   * the one outcome this feature must not have.
   */
  const selectedHere = useMemo(
    () => payable.filter((row) => selected.has(row.id)),
    [payable, selected],
  );
  /*
   * WHAT EACH ACTION COVERS, and the two are not the same set.
   *
   * A BANK FILE COVERS EVERYTHING TICKED. A direct credit batch does not care who the payee is —
   * an owner drawing their balance and a plumber settling an invoice are both a name, a number and
   * an amount — so a file made from a mixed selection is one legitimate batch, not two.
   *
   * A REMITTANCE ADVICE COVERS THE BILL PAYMENTS ONLY. It is a document about INVOICES, addressed
   * to the supplier who sent them; an owner's disbursement has no invoice to report and no
   * supplier to send it to.
   *
   * So the remittance button says how much of the selection it is about whenever that is less than
   * all of it. The bank file button never has to, because it is always about all of it.
   */
  const selectedBillPayments = selectedHere.filter((row) => row.kind === 'bill_payment');
  const someSelectionIsNotABill = selectedBillPayments.length < selectedHere.length;

  const selectedTotal = selectedHere.reduce(
    (total, row) => total + centsFromWire(row.amountCents),
    0n,
  );

  const toggle = (id: string) => {
    setSelected((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const allSelected = payable.length > 0 && selectedHere.length === payable.length;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Transactions"
        subtitle="What happened. Open one for the journals behind it"
        actions={
          <Link to="/financial/entry">
            <Button size="sm">Journal entry</Button>
          </Link>
        }
      />
      <FinancialNav />

      <div>
        <TransactionFilters
          filter={{
            ...(q === undefined ? {} : { search: q }),
            ...(kind === undefined ? {} : { kind }),
            ...(period === undefined ? {} : { period }),
            ...(account === undefined ? {} : { account }),
            ...(from === undefined ? {} : { from }),
            ...(to === undefined ? {} : { to }),
            ...(txFrom === undefined ? {} : { txFrom }),
            ...(txTo === undefined ? {} : { txTo }),
          }}
          accountName={accountName}
          onChange={(next, options) => {
            void navigate({
              search: {
                ...(next.account === undefined ? {} : { account: next.account }),
                ...(next.period === undefined ? {} : { period: next.period }),
                ...(next.kind === undefined ? {} : { kind: next.kind }),
                ...(next.search === undefined ? {} : { q: next.search }),
                ...(next.from === undefined ? {} : { from: next.from }),
                ...(next.to === undefined ? {} : { to: next.to }),
                ...(next.txFrom === undefined ? {} : { txFrom: next.txFrom }),
                ...(next.txTo === undefined ? {} : { txTo: next.txTo }),
              },
              replace: options.replace,
            });
          }}
          onClear={() => void navigate({ search: {} })}
        />
      </div>

      <Panel className="mt-4 min-w-0 p-0">
        <PanelHeader
          title={narrowed ? 'Filtered' : 'Every transaction'}
          subtitle={scope}
          className="px-4 pt-4 pb-2"
        />

        {/*
         * Said once, where the number is, rather than trusted to be obvious. Filtered to an
         * account, these rows are still the WHOLE transaction's movement — a $600 rent receipt
         * reads $600 here even when the owner it belongs to received $544.80 after the fee.
         * That is the honest figure for a transaction list and the wrong one for a statement,
         * and the difference is invisible unless somebody says so.
         */}
        {account !== undefined ? (
          <p className="mx-4 mb-2 rounded-md bg-surface-sunk px-3 py-2 text-[0.75rem] text-ink-muted">
            Every transaction that touched this account. The debit and credit columns are what the
            whole transaction moved, not this account&rsquo;s share of it.
          </p>
        ) : null}

        {transactions.isPending ? (
          <p className="px-4 pb-4 text-[0.8125rem] text-ink-muted">Loading…</p>
        ) : transactions.error && rows.length === 0 ? (
          <LoadFailed
            inline
            retry={() => void transactions.refetch()}
            retrying={transactions.isFetching}
          />
        ) : rows.length > 0 && totals !== null ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] border-collapse text-[0.8125rem]">
              <caption className="px-4 pb-2 text-left text-[0.6875rem] text-ink-subtle">
                {/*
                 * The convention, said out loud, because it is the OPPOSITE of the journals one
                 * click in — there a receipt DEBITS the trust bank, which is an asset. Both are
                 * correct; leaving the reader to work out which one a column means is how
                 * somebody reconciles against the wrong sign.
                 */}
                Debits and credits as the trust account sees them — money in is a credit. The
                journals inside each transaction use the accounting convention, where a receipt
                debits the bank.
              </caption>
              <thead>
                <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                  {/*
                   * THE COLUMN IS ONLY THERE WHEN THERE IS SOMETHING TO TICK. A column of empty
                   * cells beside two hundred receipts is a control that looks broken; on a list
                   * with no payments out on it there is no document to make from anything.
                   */}
                  {payable.length > 0 ? (
                    <th className="w-9 px-4 py-2">
                      <input
                        type="checkbox"
                        aria-label={
                          allSelected ? 'Clear every payment' : 'Select every payment listed'
                        }
                        checked={allSelected}
                        onChange={() => {
                          // ALL OF THE PAYMENTS ON SCREEN, and nothing else — never every
                          // transaction the filter matched. "Select all" reaches only the pages
                          // loaded on screen; extending it past that would tick payments nobody
                          // has read.
                          setSelected(allSelected ? new Set() : new Set(payable.map((r) => r.id)));
                        }}
                        className="size-3.5 accent-[var(--color-accent)]"
                      />
                    </th>
                  ) : null}
                  <th className="px-4 py-2 font-semibold">Ref</th>
                  <th className="px-4 py-2 font-semibold">Date</th>
                  <th className="px-4 py-2 font-semibold">Type</th>
                  <th className="px-4 py-2 font-semibold">What happened</th>
                  <th className="px-4 py-2 text-right font-semibold">Debit</th>
                  <th className="px-4 py-2 text-right font-semibold">Credit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((transaction) => (
                  <tr
                    key={transaction.id}
                    data-tap-action
                    onClick={() => setOpenTransactionId(transaction.id)}
                    className={cn(
                      'cursor-pointer border-t border-rule hover:bg-surface-sunk',
                      selected.has(transaction.id) ? 'bg-accent-soft hover:bg-accent-soft' : '',
                    )}
                  >
                    {payable.length > 0 ? (
                      <td
                        className="px-4 py-2.5 align-top"
                        // The tick is not the row. Opening a transaction to read its journals and
                        // putting it in a payment file are different acts, and a checkbox that
                        // also opened a dialog would make one of them accidental.
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {isSelectablePayment(transaction) ? (
                          <input
                            type="checkbox"
                            aria-label={`Select ${transaction.narrative}`}
                            checked={selected.has(transaction.id)}
                            onChange={() => {
                              toggle(transaction.id);
                            }}
                            className="size-3.5 accent-[var(--color-accent)]"
                          />
                        ) : null}
                      </td>
                    ) : null}
                    <td className="px-4 py-2.5 align-top">
                      {/*
                       * TX, not JN. This is the only reference in the system a customer ever has
                       * to care about — it is what goes on a statement and what gets quoted back
                       * down the phone. The JN refs live one click in.
                       */}
                      <Ref entity="transaction" sequence={transaction.refSeq} />
                    </td>
                    <td className="px-4 py-2.5 align-top tabular-nums whitespace-nowrap">
                      {formatNzDate(transaction.occurredOn)}
                    </td>
                    {/*
                     * ITS OWN COLUMN, not a note under the narrative. What kind of event this was
                     * is the thing that decides whether a management fee was charged — a
                     * `rent_receipt` charges one and a `receipt` does not — so it is worth a
                     * column somebody can scan down rather than a line they read per row.
                     */}
                    <td className="px-4 py-2.5 align-top whitespace-nowrap">
                      {transactionKindLabel(transaction.kind as TransactionKind)}
                    </td>
                    <td className="min-w-0 px-4 py-2.5 align-top">
                      <span className="block truncate">{transaction.narrative}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[0.6875rem] text-ink-subtle">
                        <span>
                          {transaction.journalCount}{' '}
                          {transaction.journalCount === 1 ? 'journal' : 'journals'}
                        </span>
                        {transaction.lateItem ? (
                          <Chip tone="warn">
                            Late — posted to {formatPeriod(transaction.periodStartsOn)}
                          </Chip>
                        ) : null}
                        {/*
                         * BOTH ENDS OF A REVERSAL, on the row, because the alternative is finding
                         * out by opening it. A reversed receipt still shows its own $600 in the
                         * debit and credit columns — the original is never touched (invariant 4)
                         * — so a scan down this list without these chips reads money that has
                         * been handed back as money the agency holds.
                         */}
                        {transaction.reversedBy ? (
                          <Chip tone="warn">Reversed by {transaction.reversedBy.ref}</Chip>
                        ) : null}
                        {transaction.reversalOf ? (
                          <Chip tone="info">Reverses {transaction.reversalOf.ref}</Chip>
                        ) : null}
                      </span>
                    </td>
                    {/*
                     * Blank, not 0.00, on the side a transaction did not move. A column of zeroes
                     * is noise to scan past; an empty cell says "not this way" at a glance, which
                     * is how a bank statement reads and why it reads quickly.
                     */}
                    <td className="px-4 py-2.5 text-right align-top tabular-nums">
                      {transaction.debitCents === '0' ? null : (
                        <Amount value={centsFromWire(transaction.debitCents)} />
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right align-top tabular-nums">
                      {transaction.creditCents === '0' ? null : (
                        <Amount value={centsFromWire(transaction.creditCents)} tone="positive" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>

              <TrustEffectRows
                debitCents={centsFromWire(totals.debitCents)}
                creditCents={centsFromWire(totals.creditCents)}
                scope={scope}
                // Ref, Date, Type, What happened — plus the tick column when it is showing.
                labelSpan={payable.length > 0 ? 5 : 4}
              />
            </table>

            {transactions.hasNextPage ? (
              <div className="flex items-center justify-center gap-3 border-t border-rule px-4 py-4">
                <p className="text-[0.8125rem] text-ink-muted">
                  Showing the most recent {rows.length} of {totals.transactionCount}.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={transactions.isFetchingNextPage}
                  onClick={() => void transactions.fetchNextPage()}
                >
                  {transactions.isFetchingNextPage
                    ? 'Loading…'
                    : transactions.isFetchNextPageError
                      ? 'Try again'
                      : 'Show more'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : transactions.data ? (
          <div className="px-4 pt-1 pb-5">
            {/*
             * Two different nothings, and telling them apart matters: an empty ledger needs a way
             * to post the first transaction, while an empty FILTER needs the filter widened. An
             * agency with two hundred transactions being told "nothing posted yet" would go
             * looking for a bug in the books.
             */}
            {narrowed ? (
              <>
                <p className="text-[0.8125rem] text-ink-muted">
                  No transaction matches that. Widen the filter — the ledger itself is unchanged.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-3"
                  onClick={() => void navigate({ search: {} })}
                >
                  Clear filters
                </Button>
              </>
            ) : (
              <>
                <p className="text-[0.8125rem] text-ink-muted">
                  Nothing posted yet. Every movement of money is a transaction, and every
                  transaction is one or more journals.
                </p>
                <Link to="/financial">
                  <Button size="sm" className="mt-3">
                    Generate the first transaction
                  </Button>
                </Link>
              </>
            )}
          </div>
        ) : null}
      </Panel>

      {/*
       * Two levels of dialog, stacked deliberately. Opening a journal from inside a transaction
       * leaves the transaction behind it, so closing the journal returns to the event rather than
       * dumping the reader back at the list having lost their place.
       */}
      <TransactionDetail
        transactionId={openTransactionId}
        onClose={() => setOpenTransactionId(null)}
        onOpenJournal={setOpenJournalId}
        // Following a reversal REPLACES what the panel is showing rather than stacking a second
        // one. The two are the same story read from either end, not a drill-down, so there is no
        // "back to the original" to preserve — the row you land on links straight back.
        onOpenTransaction={setOpenTransactionId}
      />

      <JournalDetail journalId={openJournalId} onClose={() => setOpenJournalId(null)} />

      {/*
       * THE SELECTION BAR, and it is pinned to the bottom of the window rather than sitting under
       * the table. After several pages are loaded the list is hundreds of rows deep; a bar that
       * scrolled away would leave an operator who ticked four payments at the top scrolling back
       * to find out what they had.
       *
       * IT STATES THE COUNT AND THE TOTAL BEFORE IT OFFERS ANYTHING. That is the pair a person
       * checks against the run they are re-sending, and DESIGN_SYSTEM.md §3 asks a total to say
       * what it covers — this one covers exactly the rows that are ticked and visible.
       */}
      {selectedHere.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4">
          <Glass className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-4 py-3 shadow-modal">
            <p className="text-[0.8125rem] text-ink">
              <span className="font-semibold tabular-nums">{selectedHere.length}</span>{' '}
              {selectedHere.length === 1 ? 'payment' : 'payments'} selected ·{' '}
              <Amount value={selectedTotal} className="font-semibold" />
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected(new Set());
                }}
              >
                Clear
              </Button>
              {/*
               * The remittance button is absent when nothing ticked is a bill payment. A disabled
               * one beside four owner disbursements would be asking the operator to work out why;
               * an absent one asks nothing. The bank file button is always here — everything that
               * can be ticked can go in a file.
               */}
              {selectedBillPayments.length > 0 ? (
                <RemittanceButton
                  transactionIds={selectedBillPayments.map((row) => row.id)}
                  label={
                    someSelectionIsNotABill
                      ? `Remittance · ${String(selectedBillPayments.length)} ${selectedBillPayments.length === 1 ? 'bill' : 'bills'}`
                      : 'Remittance advice'
                  }
                />
              ) : null}

              <Button
                size="sm"
                onClick={() => {
                  setMakingBankFile(true);
                }}
              >
                Make bank file
              </Button>
            </div>
          </Glass>
        </div>
      ) : null}

      {makingBankFile ? (
        <BankFileDialog
          // EVERYTHING TICKED, owners and suppliers alike — see the note on the selection above.
          source={{ kind: 'transactions', transactionIds: selectedHere.map((row) => row.id) }}
          onClose={() => {
            setMakingBankFile(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * What the totals below the list cover.
 *
 * Spelled out because the rows above are paged and filterable: a footer reading simply "Totals"
 * beside fifty loaded rows out of two hundred transactions is a figure somebody reconciles
 * against and cannot reproduce. The count comes from the server, over the whole filter rather
 * than the page.
 */
function scopeOf({
  count,
  period,
  account,
  kind,
  search,
  from,
  to,
  txFrom,
  txTo,
}: {
  count: number;
  period: string | null;
  account: string | null;
  kind: TransactionKind | null;
  search: string | null;
  from: string | null;
  to: string | null;
  txFrom: string | null;
  txTo: string | null;
}): string {
  const parts = [
    `${String(count)} ${count === 1 ? 'transaction' : 'transactions'}`,
    period ? formatPeriod(period) : 'all periods',
  ];
  // Each named when present, because a footer that does not mention the filter is a figure
  // somebody reconciles against the whole ledger and cannot reproduce.
  const range = rangeLabel(from, to);
  if (range !== null) parts.push(range);
  // The run of refs, said the way the date range is and for the same reason: one end alone is a
  // real filter, and the footer has to describe the rows that were actually totalled.
  if (txFrom !== null && txTo !== null) parts.push(`${txFrom} to ${txTo}`);
  else if (txFrom !== null) parts.push(`from ${txFrom}`);
  else if (txTo !== null) parts.push(`up to ${txTo}`);
  if (account !== null) parts.push(account);
  if (kind !== null) parts.push(transactionKindLabel(kind));
  if (search !== null) parts.push(`matching “${search}”`);
  return parts.join(' · ');
}

/**
 * The date range, as a phrase — or nothing when neither end is set.
 *
 * ONE END IS A LEGITIMATE FILTER and has to read as one. "From 01/08/2026" with no upper bound
 * is "everything since", and a footer that silently said "01/08/2026 to 01/08/2026" or dropped
 * the half that was set would be describing a different set of rows than the one totalled.
 *
 * `formatNzDate` rather than the ISO string, for the same reason the Date column uses it: New
 * Zealand writes the day first, and `2026-08-01` on a screen is a database row showing through.
 */
function rangeLabel(from: string | null, to: string | null): string | null {
  if (from !== null && to !== null) return `${formatNzDate(from)} to ${formatNzDate(to)}`;
  if (from !== null) return `from ${formatNzDate(from)}`;
  if (to !== null) return `up to ${formatNzDate(to)}`;
  return null;
}
